import { Injectable } from '@angular/core';
import { ApiService } from '@quorum/api';
import { MenuServiceItem, ServiceVisitRecord } from '@quorum/models/xs-misc';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

const BASE_PATH = 'v/2/fixed-operations/service-visits';

@Injectable({
  providedIn: 'root',
})
export class ServiceVisitRecordService {
  constructor(private apiService: ApiService) {}

  getServiceVisitRecord(
    destinationId: string,
    appointmentId?: string,
    workOrderId?: string
  ): Observable<ServiceVisitRecord | null> {
    if (workOrderId) {
      return this.apiService.get(`${BASE_PATH}/${destinationId}/${workOrderId}`).pipe(
        catchError((err) => {
          if (err?.statusCode === 404) {
            // Not found, return null to indicate no existing record
            return of(null);
          }
          return throwError(err);
        })
      );
    }
    if (appointmentId) {
      return this.apiService.get(`${BASE_PATH}/${destinationId}?appointmentId=${appointmentId}`).pipe(
        map((data: ServiceVisitRecord[]) => (data && data.length > 0 ? data[0] : null)),
        catchError((err) => {
          if (err?.statusCode === 404) {
            // Not found, return null to indicate no existing record
            return of(null);
          }
          return throwError(err);
        })
      );
    }
    return of(null);
  }

  createServiceVisitRecord(data: Partial<ServiceVisitRecord>): Observable<ServiceVisitRecord> {
    if (!data.workOrderId) {
      return throwError(new Error('workOrderId is required to create a ServiceVisitRecord'));
    }
    const record: Record<string, unknown> = {
      workOrderId: data.workOrderId,
      status: data.status || '0',
      statusDescription: data.statusDescription || 'Open',
      ...(data.appointmentId ? { appointmentId: data.appointmentId } : {}),
      ...(data.appointmentType ? { appointmentType: data.appointmentType } : {}),
      lastUpdatedBy: 'ANALYTIC_EVENT',
    };
    return this.apiService.post(BASE_PATH, record);
  }

  patchServiceVisitRecord(
    destinationId: string,
    workOrderId: string,
    version: number,
    fields: Record<string, unknown>
  ): Observable<ServiceVisitRecord> {
    return this.apiService.patch(`${BASE_PATH}/${destinationId}/${workOrderId}`, {
      version,
      ...fields,
    });
  }

  getOrCreateServiceVisitRecord(
    destinationId: string,
    appointmentId?: string,
    workOrderId?: string
  ): Observable<ServiceVisitRecord | null> {
    return this.getServiceVisitRecord(destinationId, appointmentId, workOrderId).pipe(
      switchMap((svr: ServiceVisitRecord | null) => {
        if (!svr) {
          if (!workOrderId) {
            // No existing record and no work order to key a new one against (for example,
            // viewing the OEM menu from an appointment that has not been turned into a work
            // order yet). A ServiceVisitRecord is keyed by workOrderId and cannot be created
            // without one, so there is nothing to track here. Emit null instead of throwing,
            // otherwise a routine appointment view surfaces as a console/RUM error.
            return of(null);
          }
          return this.createServiceVisitRecord({
            destinationId,
            workOrderId,
            ...(appointmentId ? { appointmentId } : {}),
          });
        }
        return of(svr);
      }),
      take(1)
    );
  }

  recordInitialSnapshot(svr: ServiceVisitRecord): Observable<ServiceVisitRecord> {
    if (svr.initialSnapshot) {
      return of(svr);
    }
    return this.apiService.post(`${BASE_PATH}/${svr.workOrderId}/initial-snapshot`, {}).pipe(
      map((result: ServiceVisitRecord | { message?: string }) => {
        if (result && (result as ServiceVisitRecord).workOrderId && (result as ServiceVisitRecord).destinationId) {
          return result as ServiceVisitRecord;
        }
        return svr;
      }),
      catchError(() => of(svr))
    );
  }

  recordAdvisorMobileCheckIn(svr: ServiceVisitRecord): Observable<ServiceVisitRecord> {
    const isTouchscreen = this.isTouchscreenDevice();
    return this.patchServiceVisitRecord(svr.destinationId, svr.workOrderId, svr.version, {
      communications: {
        ...svr.communications,
        advisorMobileCheckIn: {
          usedMobileDevice: isTouchscreen,
          ...(isTouchscreen ? { checkInAt: new Date().toISOString() } : {}),
        },
      },
    }).pipe(
      map((patched: ServiceVisitRecord | { message?: string }) => {
        // Backend may return a 200 with a `{ message: ... }` body when the initialSnapshot
        // was already captured. In that no-op case, fall back to the original `svr` so
        // downstream consumers always receive a ServiceVisitRecord.
        if (patched && (patched as ServiceVisitRecord).workOrderId && (patched as ServiceVisitRecord).destinationId) {
          return patched as ServiceVisitRecord;
        }
        return svr;
      })
    );
  }

  // Communications tracking moving to backend
  // recordAppointmentConfirmationSent
  // recordDigitalCheckInSent
  // recordIcvrSent
  // recordRoReviewSent
  // recordEstimateSent
  // recordPostBookletSent

  recordServiceVisitTracking(svr: ServiceVisitRecord): Observable<ServiceVisitRecord> {
    return this.patchServiceVisitRecord(svr.destinationId, svr.workOrderId, svr.version, {
      communications: {
        ...svr.communications,
        serviceVisitTracking: true,
      },
    });
  }

  updateMenuData(svr: ServiceVisitRecord, items: MenuServiceItem[]): Observable<ServiceVisitRecord> {
    return this.patchServiceVisitRecord(svr.destinationId, svr.workOrderId, svr.version, {
      menuData: items,
    });
  }

  isTouchscreenDevice(): boolean {
    let isTouchscreen = false;

    if ('maxTouchPoints' in navigator) {
      isTouchscreen = navigator.maxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
      if (mQ && mQ.media === '(pointer:coarse)') {
        isTouchscreen = !!mQ.matches;
      } else if ('orientation' in window) {
        isTouchscreen = true;
      } else {
        const UA = (navigator as Navigator).userAgent;
        isTouchscreen =
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
      }
    }

    return isTouchscreen;
  }
}
